import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';
import { useAuth } from '../store/auth';

function Stat({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="card p-5">
      <div className="text-xs text-slate-500 uppercase tracking-wider">{label}</div>
      <div className="mt-2 text-2xl font-semibold text-slate-900">{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

export default function DashboardPage() {
  const actor = useAuth((s) => s.actor);
  const isSuper = actor?.type === 'SUPER_ADMIN';
  const isAgent = actor?.type === 'AGENT';

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', actor?.type],
    queryFn: async () => {
      const path = isSuper ? '/dashboard/super-admin' : isAgent ? '/dashboard/agent' : '/dashboard/tenant';
      return (await api.get(path)).data;
    },
  });

  return (
    <div className="p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">Dashboard</h1>
        <p className="text-sm text-slate-500">{isSuper ? 'All-tenant overview' : isAgent ? 'Your queue, today' : 'Your team, today'}</p>
      </div>

      {isLoading && <div className="card p-6 text-slate-500">Loading…</div>}

      {data && isSuper && (
        <div className="grid grid-cols-4 gap-4">
          <Stat label="Tenants" value={data.tenants} />
          <Stat label="Open tickets" value={data.openTickets} />
          <Stat label="Total tickets" value={data.tickets} />
          <Stat label="Agents" value={data.agents} />
        </div>
      )}

      {data && !isSuper && !isAgent && (
        <>
          <div className="grid grid-cols-4 gap-4">
            <Stat label="Open" value={data.openCount} />
            <Stat label="SLA breached" value={data.breachedCount} />
            <Stat label="Resolved today" value={data.resolvedToday} />
            <Stat label="CSAT avg" value={data.csatAvg ? Number(data.csatAvg).toFixed(2) : '—'} sub={`${data.csatCount} responses`} />
          </div>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div className="card p-5">
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-3">By status</div>
              <ul className="space-y-1 text-sm">
                {Object.entries(data.ticketsByStatus ?? {}).map(([k, v]) => (
                  <li key={k} className="flex justify-between"><span className="text-slate-600">{k}</span><span className="font-medium">{String(v)}</span></li>
                ))}
              </ul>
            </div>
            <div className="card p-5">
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-3">Open by priority</div>
              <ul className="space-y-1 text-sm">
                {Object.entries(data.openByPriority ?? {}).map(([k, v]) => (
                  <li key={k} className="flex justify-between"><span className="text-slate-600">{k}</span><span className="font-medium">{String(v)}</span></li>
                ))}
              </ul>
            </div>
          </div>
        </>
      )}

      {data && isAgent && (
        <>
          <div className="grid grid-cols-4 gap-4">
            <Stat label="My open" value={data.open} />
            <Stat label="Resolved today" value={data.resolvedToday} />
            <Stat label="My breached" value={data.breached} />
            <Stat label="My CSAT" value={data.csatAvg ? Number(data.csatAvg).toFixed(2) : '—'} />
          </div>
          <div className="card p-5 mt-4">
            <div className="text-xs text-slate-500 uppercase tracking-wider mb-3">My queue</div>
            <ul className="divide-y divide-slate-100">
              {(data.mine ?? []).map((t: { id: string; ticketNo: string; subject: string; status: string; priority: string }) => (
                <li key={t.id} className="py-2 flex justify-between text-sm">
                  <div>
                    <span className="text-slate-500 mr-2">{t.ticketNo}</span>
                    {t.subject}
                  </div>
                  <div className="flex gap-2"><span className="badge bg-slate-100 text-slate-700">{t.status}</span><span className="badge bg-amber-100 text-amber-800">{t.priority}</span></div>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
